'use client';

/**
 * Section-level tab bar wrapper — Phase 6.5 nav IA remediation.
 *
 * Bridges the framework-agnostic `SubPageTabs` primitive (in
 * `@statura/ui`) with the App Router + Next.js `<Link>`, and consults
 * the `SUB_PAGE_TABS_BY_PARENT` registry to decide which tabs (if any)
 * to render for the current pathname.
 *
 * Mounted ONCE inside the Nav v2 dashboard shell above
 * `{children}` — most pages render nothing (registry returns null);
 * pages under a registered section render a horizontal tab bar.
 *
 * RBAC + module filtering (Codex Phase 6.5 pass 2 P2): each destination is
 * filtered through the same full gate chain the Nav v2 sidebar uses:
 *   - `isRoleAllowedForPath(href, liveRole)` — RBAC
 *   - `isModuleNavigationVisible(href, enabledModules)` — entitlement
 *   - `isDashboardProductionSurfaceVisibleForTenant(href, ctx)` — production surface
 * A user who can open /payroll/queue but not /payroll/agency-invoices
 * sees only the tabs they can use.
 *
 * Telemetry: tab clicks fire `nav_v2_item_clicked` with
 * `source: 'section-tabs'` for per-surface metrics.
 */

import { useWhsProgram, whsHref } from '@/lib/compliance/whs-href';
import { RosterSectionNavClient } from '@/components/roster/RosterSectionNavClient';
import { useMemo, type ComponentProps } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { SubPageTabs, resolveActiveTab, type SubPageTab } from '@statura/ui';
import { NEW_PAYROLL_NAV_TABS } from '@/lib/payroll/payroll-nav-sections';
import { isPayrollStatusReviewer } from '@/lib/payroll/coordinator-status';
import { resolveSubPageTabs } from '@/lib/navigation/sub-page-tabs-registry';
import { trackNavV2Event } from '@/lib/nav-v2/telemetry';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { useModules } from '@/contexts/module-context';
import { useGovernmentIntegrations } from '@/contexts/government-integration-context';
import { isGovernmentIntegrationPathVisible } from '@/lib/integrations/government/enablement-types';
import { CERTIFICATION_SELF_SERVICE_ROLES } from '@/lib/workforce/certification-access';
import { isRoleAllowedForPath } from '@/lib/route-access';
import { isCasesHrefVisible } from '@/lib/hr/cases-access';
import { isModuleNavigationVisible } from '@/lib/module-access';
import { isDashboardProductionSurfaceVisibleForTenant } from '@/lib/production-surface-access';
import { isLaunchPrimaryNavigationVisible } from '@/lib/launch-navigation';
import { isTenantNavigationPathVisible } from '@/lib/navigation/tenant-navigation';
import { isFrontlineSelfServiceNavigationVisible } from '@/lib/navigation/frontline-self-service';
import { rosterSectionDispatchHref } from '@/lib/roster/dispatch-view-persistence';
import {
  ROSTER_SECTION_SEGMENTS,
  rosterNavigationPath,
  type RosterPrimaryGroupId,
} from '@/lib/roster/roster-navigation-ia';
import {
  sectionTabsMoreRequested,
  shouldPaintSectionTabFirstPaint,
} from '@/lib/navigation/section-tabs-first-paint';
import { useOrgLabels } from '@/contexts/org-labels-context';
import { isHrefVisibleForProviderProgram } from '@/lib/navigation/program-route-policy';

type ResolvedSubPageTabs = NonNullable<ReturnType<typeof resolveSubPageTabs>>;

// These consolidated compliance pages choose content using a single view key.
// Match only that key so register filters never affect the current-page marker.
function complianceSectionPath(pathname: string, search: string, tabs: readonly SubPageTab[]) {
  const viewKey =
    pathname === '/compliance/continuous-improvement'
      ? 'view'
      : pathname === '/compliance/complaints' || pathname === '/compliance/standards'
        ? 'tab'
        : null;
  if (!viewKey) return pathname;
  const selected = new URLSearchParams(search).get(viewKey);
  return (
    tabs.find((tab) => {
      const [path, query] = tab.href.split('?');
      return path === pathname && query && new URLSearchParams(query).get(viewKey) === selected;
    })?.href ?? pathname
  );
}

function complaintsSectionHref(href: string, pathname: string, search: string) {
  const [path, query] = href.split('?');
  if (pathname !== '/compliance/complaints' || path !== pathname) return href;
  const params = new URLSearchParams(search);
  const tab = new URLSearchParams(query).get('tab');
  if (tab) params.set('tab', tab);
  else params.delete('tab');
  const nextSearch = params.toString();
  return nextSearch ? `${path}?${nextSearch}` : path;
}

/**
 * Resolve visible tab destinations without leaving a group pointed at a hidden
 * landing page. Roster's five primary groups are stable task labels; their
 * landing href is the first visible child for the current actor. Other section
 * registries retain their ordinary per-tab filtering.
 */
export function resolveVisibleSectionTabs(
  resolved: ResolvedSubPageTabs | null,
  isPathVisible: (href: string) => boolean,
  shiftSwapsEnabled = false,
): ReadonlyArray<SubPageTab> {
  if (!resolved) return [];
  if (resolved.parentHref === '/people') {
    return resolved.tabs.flatMap((tab) => {
      const candidates = tab.id === 'onboarding' ? [tab.href, '/people/offboarding'] : [tab.href];
      const href = candidates.find(isPathVisible);
      return href ? [{ ...tab, href }] : [];
    });
  }
  if (resolved.parentHref !== '/roster')
    return resolved.tabs.filter((tab) => isPathVisible(tab.href));

  return resolved.tabs.flatMap((tab) => {
    const segments = ROSTER_SECTION_SEGMENTS[tab.id as RosterPrimaryGroupId];
    const candidates = segments?.length ? segments : [tab];
    const landing = candidates.find(
      (candidate) =>
        (!('featureFlag' in candidate) ||
          candidate.featureFlag !== 'shift_swaps' ||
          shiftSwapsEnabled) &&
        isPathVisible(candidate.href.split('?')[0] ?? ''),
    );
    return landing
      ? [{ ...tab, href: landing.href, activeFor: [tab.href, ...(tab.activeFor ?? [])] }]
      : [];
  });
}

export function NavV2SectionTabs({ pathname }: { pathname: string }) {
  const governmentIntegrations = useGovernmentIntegrations();
  const searchParams = useSearchParams();
  const whsProgram = useWhsProgram();
  const more = sectionTabsMoreRequested(searchParams.get('more'));
  const liveRole = useResolvedAuthUser()?.role;
  const orgLabels = useOrgLabels();
  const providerType = orgLabels.providerType;
  const {
    enabledModules,
    billingPlan,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    shiftSwapsEnabled,
    payrollRebuildOn,
  } = useModules();
  const resolved = useMemo(() => {
    const value = resolveSubPageTabs(pathname);
    if (value?.parentHref === '/people' && payrollRebuildOn && isPayrollStatusReviewer(liveRole))
      return {
        ...value,
        tabs: [...value.tabs, { id: 'payroll-status', label: 'Payroll status', href: '/payroll' }],
      };
    return payrollRebuildOn && value?.parentHref === '/payroll'
      ? { ...value, tabs: NEW_PAYROLL_NAV_TABS }
      : value;
  }, [pathname, payrollRebuildOn, liveRole]);
  const visibleTabs = useMemo<ReadonlyArray<SubPageTab>>(() => {
    return resolveVisibleSectionTabs(
      resolved,
      (href) =>
        Boolean(
          isRoleAllowedForPath(href, liveRole, billingPlan) &&
          isCasesHrefVisible(href, liveRole) &&
          isHrefVisibleForProviderProgram(
            href,
            orgLabels.providerProgramContext ?? providerType,
            undefined,
            tenantId,
          ) &&
          isModuleNavigationVisible(href, enabledModules, { isDemoTenant }) &&
          isDashboardProductionSurfaceVisibleForTenant(href, {
            tenantId,
            isDemo: isDemoTenant,
          }) &&
          isLaunchPrimaryNavigationVisible(href, visibleGoLiveScope, { isDemoTenant }) &&
          isTenantNavigationPathVisible(href, tenantHiddenNavigationPaths) &&
          isFrontlineSelfServiceNavigationVisible({
            href,
            role: liveRole,
            enabled: frontlineSelfServiceOnly === true,
          }) &&
          isGovernmentIntegrationPathVisible(href, governmentIntegrations, liveRole) &&
          shouldPaintSectionTabFirstPaint({
            parentHref: resolved?.parentHref ?? '',
            tabHref: href,
            pathname,
            more,
          }),
        ),
      shiftSwapsEnabled,
    );
  }, [
    resolved,
    liveRole,
    billingPlan,
    providerType,
    orgLabels.providerProgramContext,
    enabledModules,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    governmentIntegrations,
    shiftSwapsEnabled,
    pathname,
    more,
  ]);

  // This leaf is a self-only surface even when the same nurse has unrelated
  // People authority (for example supervision). Do not offer manager navigation.
  if (
    (pathname === '/people/compliance/certifications' ||
      pathname.startsWith('/people/compliance/certifications/')) &&
    CERTIFICATION_SELF_SERVICE_ROLES.includes(liveRole ?? '')
  )
    return null;

  // Hide entirely when no tabs survive filtering — avoids a stub bar.
  if (visibleTabs.length === 0) return null;
  // People retains Home/current even for a single admitted destination.
  // Roster is different from generic sections: its
  // group label may be the only discoverable path to a direct regulated child
  // (for example Cover & attendance → Handovers), so one surviving group must remain.
  if (visibleTabs.length < 2 && !['/roster', '/people'].includes(resolved?.parentHref ?? ''))
    return null;

  if (resolved?.parentHref === '/roster')
    return <RosterSectionNavClient groups={visibleTabs} providerType={providerType} />;

  const Tabs = resolved?.parentHref === '/people' ? PeopleSectionTabs : SubPageTabs;

  return (
    <Tabs
      tabs={visibleTabs}
      currentPath={
        resolved?.parentHref === '/roster'
          ? rosterNavigationPath(pathname, searchParams.toString())
          : complianceSectionPath(pathname, searchParams.toString(), resolved?.tabs ?? [])
      }
      linkRender={({ href, className, 'aria-current': ariaCurrent, children }) => (
        <Link
          key={href}
          href={rosterSectionDispatchHref(
            pathname.startsWith('/compliance/whs') && href.startsWith('/compliance/whs')
              ? whsHref(href, whsProgram ?? searchParams.get('program'))
              : complaintsSectionHref(href, pathname, searchParams.toString()),
            pathname,
            searchParams.toString(),
            null,
          )}
          className={`${className} min-h-8 focus-visible:outline-focus`}
          aria-current={ariaCurrent}
          onClick={() =>
            trackNavV2Event('nav_v2_item_clicked', {
              source: 'section-tabs',
              route: href,
            })
          }
        >
          {children}
        </Link>
      )}
    />
  );
}

/** Widths are measured from the same typography as the links, never label estimates. */
export function partitionPeopleTabs(
  tabs: readonly SubPageTab[],
  currentPath: string,
  availableWidth: number,
  widths: readonly number[],
  moreWidth: number,
  phone: boolean,
) {
  const active = resolveActiveTab(tabs, currentPath);
  const home = tabs.findIndex((tab) => tab.id === 'home');
  const current = tabs.findIndex((tab) => tab.href === active && tab.id !== 'home');
  const secondary = current >= 0 ? current : tabs.findIndex((tab) => tab.id === 'onboarding');
  const selected = new Set<number>();
  if (home >= 0) selected.add(home);
  const total = widths.reduce((sum, width) => sum + width, 0);
  if (!phone && total <= availableWidth) return { visible: tabs, hidden: [] };
  let used = (home >= 0 ? (widths[home] ?? 0) : 0) + moreWidth;
  if (secondary >= 0 && used + (widths[secondary] ?? 0) <= availableWidth) {
    selected.add(secondary);
    used += widths[secondary] ?? 0;
  }
  if (!phone) {
    for (let index = 0; index < tabs.length; index++) {
      if (selected.has(index)) continue;
      if (used + (widths[index] ?? 0) > availableWidth) break;
      selected.add(index);
      used += widths[index] ?? 0;
    }
  }
  return {
    visible: tabs.filter((_, index) => selected.has(index)),
    hidden: tabs.filter((_, index) => !selected.has(index)),
  };
}

/** People uses the same inline overflow and phone scrolling as every section. */
export function PeopleSectionTabs(props: ComponentProps<typeof SubPageTabs>) {
  return <SubPageTabs {...props} navigationLabel="People navigation" />;
}
